import React from 'react';
import PromptCard from '../PromptCard';

interface WorkflowGuideProps {
  onPromptClick: (prompt: string) => void;
}

const WorkflowGuide: React.FC<WorkflowGuideProps> = ({ onPromptClick }) => {
  const datasetPrompts = [
    "Download the Iris dataset and create a scatter plot of sepal_length vs petal_length colored by species",
    "Download the Titanic dataset and create a 2x2 grid: survival by class, age distribution, fare box plot, and gender breakdown",
    "Download the Wine Quality dataset and create a box plot of alcohol content by quality rating",
    "Download the Boston Housing dataset and create a scatter plot of rooms vs price colored by crime rate",
    "Download the Diabetes dataset and create a bar chart of average glucose levels by age group",
    "Download the Mall Customers dataset and create a scatter plot of annual income vs spending score",
    "Download the Heart Disease dataset and create a 2x2 grid: age distribution, cholesterol by target, max heart rate, and chest pain types",
    "Download the Mushroom dataset and create a bar chart of edible vs poisonous count by cap shape",
    "Download the Pima Indians Diabetes dataset and create a correlation heatmap of all features",
    "Download the Tips dataset and create a box plot of tip amount by day of week",
  ];

  const searchPrompts = [
    "Find top 100 universities for computer science and export as CSV",
    "Search for Python developer job listings in India and create a salary distribution chart",
    "Get scholarship opportunities for engineering students with deadlines and eligibility",
    "Find online course ratings from review sites and compare platforms",
  ];

  const scrapePrompts = [
    "Scrape laptop specifications from comparison sites and create a price vs performance chart",
    "Find and compare programming bootcamp prices and durations",
    "Search for remote internship listings and export with company, role, and stipend",
  ];

  const vizPrompts = [
    "Create a pie chart of global programming language popularity",
    "Generate a bar chart comparing average salaries of top 10 tech roles",
    "Create a line chart showing smartphone market share trends over 5 years",
  ];

  const timeSeriesPrompts = [
    "Download Bitcoin price history and create a dual-axis chart with price and 30-day moving average, highlight the 2021 peak",
    "Download S&P 500 historical data and create a line chart of closing price over time",
    "Download COVID-19 dataset and create a line chart of total cases for top 10 countries",
    "Download world population data and create a line chart from 1960 to 2020",
  ];

  return (
    <div className="documentation-content">
      <h1>Workflow - Data & Visualization</h1>
      <p className="intro-text">
        One prompt to search, download datasets, create CSVs, and generate charts.
        Click any prompt to try it!
      </p>

      <div className="section">
        <h2>Download & Visualize Datasets</h2>
        <p className="section-desc">Get real datasets and create charts automatically</p>
        {datasetPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Search & Export</h2>
        <p className="section-desc">Search the web and export structured data as CSV</p>
        {searchPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Scrape & Compare</h2>
        <p className="section-desc">Extract data from websites and compare</p>
        {scrapePrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Time Series & Trends</h2>
        <p className="section-desc">Historical data with line charts and moving averages</p>
        {timeSeriesPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Quick Charts</h2>
        <p className="section-desc">Generate visualizations from general knowledge</p>
        {vizPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="tips-section">
        <h3>Workflow Features</h3>
        <ul>
          <li><strong>CSV Download</strong>: Every request generates a downloadable file</li>
          <li><strong>Live Charts</strong>: Bar, scatter, pie, line, box plots — auto-generated</li>
          <li><strong>Web Search</strong>: Finds data from across the internet</li>
          <li><strong>Web Scraping</strong>: Extracts structured data from pages</li>
          <li><strong>Multi-Step</strong>: Search, download, process, visualize — all in one prompt</li>
        </ul>
      </div>

      <div className="tips-section">
        <h3>Tips for Better Results</h3>
        <ul>
          <li>Be specific about the chart type you want (scatter, bar, pie, line)</li>
          <li>Mention columns or fields for the axes (e.g. "sepal_length vs petal_length")</li>
          <li>For well-known datasets, just name them (Iris, Titanic, Wine, etc.)</li>
          <li>Ask for "colored by" or "grouped by" for richer visualizations</li>
        </ul>
      </div>
    </div>
  );
};

export default WorkflowGuide;
